import React, { useState } from 'react';
import { FiArrowLeft, FiSearch, FiX, FiChevronRight } from 'react-icons/fi';
import { PiCheckBold } from 'react-icons/pi';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useUpdateProfileMutation } from '../../api/auth';
import { signupStore } from '../../utils/signupStore';
import { useGetGenresQuery, useCreateGenreMutation } from '../../api/genres';
import { AuthHeader } from './AuthHeader';

// Hardcoded GENRES removed, now using useGetGenresQuery

export const MusicStylePage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const storedGenres = signupStore.get().genres || [];
  const storedCustomGenres = signupStore.get().customGenres || [];
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>(storedGenres);
  // Genres created by the user during signup – shown optimistically, created via API post-auth
  const [customGenres, setCustomGenres] = useState<string[]>(storedCustomGenres);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  const profileMutation = useUpdateProfileMutation();
  const { data: genresData, isLoading: isLoadingGenres } = useGetGenresQuery();
  const createGenreMutation = useCreateGenreMutation();
  const queryClient = useQueryClient();

  const GENRES = genresData?.data?.map((g) => g.name) || [];
  // Custom genres pinned to the top, followed by API genres (dedup by name)
  const ALL_GENRES = [...customGenres.filter((cg) => !GENRES.includes(cg)), ...GENRES];

  const isSignupFlow = signupStore.isActive();

  const handleCreateGenre = async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    // During signup there's no token yet – add optimistically and defer the API call
    if (isSignupFlow) {
      if (!customGenres.includes(trimmed)) {
        setCustomGenres((prev) => [...prev, trimmed]);
      }
      setSelectedGenres((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
      setSearchQuery('');
      return;
    }

    // Post-login: user is authenticated, create via API immediately
    try {
      const newGenre = await createGenreMutation.mutateAsync(trimmed);
      setCustomGenres((prev) => {
        const exists = prev.some((genre) => genre.toLowerCase() === newGenre.name.toLowerCase());
        return exists ? prev : [...prev, newGenre.name];
      });
      setSelectedGenres((prev) => [...prev, newGenre.name]);
      await queryClient.invalidateQueries({ queryKey: ['genres'] });
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to create genre:', error);
    }
  };

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
  };

  const filteredGenres = ALL_GENRES.filter((genre) =>
    genre.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const trimmedSearchQuery = searchQuery.trim();
  // Whether the exact search term doesn't match anything (to show the "Create" CTA)
  const noExactMatch =
    trimmedSearchQuery.length > 0 &&
    !ALL_GENRES.some((g) => g.toLowerCase() === trimmedSearchQuery.toLowerCase());

  const handleContinue = async () => {
    if (isSignupFlow) {
      // Save genres (and any user-created custom ones) to store; API calls happen post-auth
      signupStore.set({ genres: selectedGenres, customGenres });
      navigate({ to: '/search-location' });
      return;
    }

    // Post-login flow – update profile via API
    setIsLoadingProfile(true);
    try {
      await profileMutation.mutateAsync({ genres: selectedGenres });
      updateUser({ genres: selectedGenres });
      navigate({ to: '/' });
    } catch (error) {
      console.error('Failed to update music styles:', error);
      updateUser({ genres: selectedGenres });
      navigate({ to: '/' });
    } finally {
      setIsLoadingProfile(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-150 mx-auto px-6 py-20">
        {/* Navigation Actions */}
        <div className="w-full flex justify-between items-center mb-6">
          <button
            onClick={() => window.history.back()}
            className="w-10 h-10 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-zinc-300" size={18} />
          </button>
          <button
            onClick={() => navigate({ to: isSignupFlow ? '/search-location' : '/' })}
            className="text-[#3B82F6] hover:text-blue-400 font-medium text-[15px] flex items-center gap-1 transition-colors group cursor-pointer"
          >
            Skip{' '}
            <FiChevronRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>

        <div className="w-full text-center mb-8">
          <h1
            className="text-white text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            What's your music style?
          </h1>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] font-medium">
            Select genres that match your musical taste
          </p>
        </div>

        <div className="w-full flex flex-col gap-6">
          {/* Search Input */}
          <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-2xl h-14 px-5 group focus-within:border-blue-500/50 transition-colors shadow-lg">
            <FiSearch className="text-zinc-500 mr-3 shrink-0" size={20} />
            <input
              type="text"
              placeholder="Search genres"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none text-white text-[16px] placeholder:text-zinc-600 w-full focus:outline-none focus:ring-0 font-medium min-w-0"
            />
            {noExactMatch && (
              <button
                onClick={handleCreateGenre}
                disabled={!isSignupFlow && createGenreMutation.isPending}
                className="ml-3 shrink-0 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-300 text-[12px] font-semibold py-1.5 px-3 rounded-full transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {!isSignupFlow && createGenreMutation.isPending ? (
                  <>
                    <div className="w-3 h-3 border-2 border-purple-300/30 border-t-purple-300 rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  `Create "${trimmedSearchQuery}"`
                )}
              </button>
            )}
          </div>

          {/* Selection Status */}
          <div className="flex items-center justify-between px-1">
            <div className="bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20 py-1 px-4 rounded-full text-[13px] font-semibold">
              {selectedGenres.length} genre{selectedGenres.length !== 1 ? 's' : ''} selected
            </div>
            <button
              onClick={() => setSelectedGenres([])}
              className="text-zinc-500 hover:text-white text-[13px] font-medium flex items-center gap-1.5 transition-colors cursor-pointer group"
            >
              Clear all <FiX size={14} className="transition-transform group-hover:rotate-90" />
            </button>
          </div>

          {/* Genre List */}
          <div className="flex flex-col gap-1 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
            {isLoadingGenres ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                <p className="text-zinc-500 text-[14px]">Loading genres...</p>
              </div>
            ) : (
              <>
                {filteredGenres.map((genre) => {
                  const isSelected = selectedGenres.includes(genre);
                  const isCustom = customGenres.includes(genre);
                  return (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className="flex items-center justify-between w-full py-4 px-2 hover:bg-white/5 rounded-xl transition-colors text-left group cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`text-[16px] font-medium transition-colors ${isSelected ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-200'}`}
                        >
                          {genre}
                        </span>
                        {isCustom && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-purple-500/20 text-purple-300 border border-purple-500/30 leading-none">
                            New
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-[#22C55E] flex items-center justify-center shadow-lg shadow-green-500/20 scale-anim">
                          <PiCheckBold className="text-white" size={14} />
                        </div>
                      )}
                    </button>
                  );
                })}

                {noExactMatch && filteredGenres.length === 0 && (
                  <p className="text-zinc-500 text-[13px] px-2 mt-1">
                    No match for "{trimmedSearchQuery}"
                  </p>
                )}
              </>
            )}
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={isLoadingProfile || isLoadingGenres}
            className="mt-4 h-14 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-[16px] rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-50"
          >
            {isLoadingProfile ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .scale-anim {
          animation: scaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};
